import { useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { t } from '@/shared/lib/i18n'
import { getCsrfToken } from '@/shared/lib/utils'
import type { WebInstrumentsLotComparison } from '@/shared/types/web'
import { LotCompareContent } from './LotCompareContent'

export interface LotCompareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  instrumentId: number
  lotDetailId: number
}

export const LotCompareDialog = ({
  open,
  onOpenChange,
  instrumentId,
  lotDetailId,
}: LotCompareDialogProps) => {
  const [comparison, setComparison] = useState<WebInstrumentsLotComparison | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const controllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    controllerRef.current = new AbortController()

    fetch(`/instruments/${instrumentId}/lots/${lotDetailId}/compare`, {
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      signal: controllerRef.current.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json()
      })
      .then((data) => {
        setComparison(data.comparison)
        setLoading(false)
      })
      .catch((fetchError) => {
        if (fetchError.name !== 'AbortError') {
          setError(fetchError.message)
          setLoading(false)
        }
      })

    return () => controllerRef.current?.abort()
  }, [instrumentId, lotDetailId])

  const dialogTitle = comparison
    ? `${t('instruments.lotsCompare.title')}: ${comparison.primaryLotDetail.lotNumber ?? comparison.primaryLotDetail.lotName ?? ''}`
    : t('instruments.lotsCompare.title')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              <span className="text-sm">{t('instruments.lotsCompare.loading')}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive text-sm">
            {error}
          </div>
        )}

        {comparison && <LotCompareContent comparison={comparison} />}
      </DialogContent>
    </Dialog>
  )
}
